import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useChime — an opt-in audio alert with a persisted toggle.
 *
 * The tone is synthesised with the Web Audio API rather than loaded from a
 * file: no asset to ship, no 404 to debug, no request in the path between
 * something happening and the person hearing about it.
 *
 * Browsers block audio until the user has interacted with the page, and the
 * AudioContext starts suspended. Both are handled by creating the context
 * lazily on the first play and resuming it — and by having the toggle play a
 * test tone, which doubles as the gesture that unlocks playback. Someone who
 * turns the chime on and hears nothing has learned nothing; someone who turns
 * it on and hears it knows it works.
 *
 * `storageKey` keeps the two chimes independent — a student's preference and an
 * instructor's are different settings on possibly the same machine.
 */
export function useChime(storageKey, { defaultOn = false } = {}) {
  const [enabled, setEnabledState] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved === null ? defaultOn : saved === "true";
    } catch {
      return defaultOn;
    }
  });

  const ctxRef = useRef(null);

  useEffect(() => {
    return () => {
      try {
        ctxRef.current?.close();
      } catch {
        /* already closed */
      }
      ctxRef.current = null;
    };
  }, []);

  /** Two descending notes — noticeable across a room, not startling. */
  const play = useCallback((force = false) => {
    if (!enabled && !force) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      if (!ctxRef.current || ctxRef.current.state === "closed") {
        ctxRef.current = new AudioCtx();
      }
      const ctx = ctxRef.current;
      if (ctx.state === "suspended") ctx.resume();

      const now = ctx.currentTime;
      [
        { freq: 880, at: 0, dur: 0.18 },
        { freq: 660, at: 0.16, dur: 0.32 },
      ].forEach(({ freq, at, dur }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, now + at);
        // Ramp in and out — an abrupt start and stop produces an audible click.
        gain.gain.setValueAtTime(0.0001, now + at);
        gain.gain.exponentialRampToValueAtTime(0.25, now + at + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + at + dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + at);
        osc.stop(now + at + dur + 0.02);
      });
    } catch {
      /* audio unavailable — never let a missing chime break the page */
    }
  }, [enabled]);

  const setEnabled = useCallback((next) => {
    setEnabledState(next);
    try {
      localStorage.setItem(storageKey, String(next));
    } catch {
      /* storage disabled — the preference just won't survive a reload */
    }
    // Turning it on plays once, both to confirm it works and to unlock audio
    // while we still have the click that enabled it.
    if (next) play(true);
  }, [storageKey, play]);

  const toggle = useCallback(() => setEnabled(!enabled), [enabled, setEnabled]);

  return { enabled, setEnabled, toggle, play };
}

export default useChime;
